import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { BellRing, CalendarPlus, Check } from "lucide-react";
import { Card } from "../ui/CCard";
import api from "../../api";

// Self-fetching "due for follow-up" queue, shown on both TherapistDashboard.js
// (doctor-scoped) and Dashboard.js (admin/org-wide, filterable). Deliberately
// not folded into either page's existing Promise.all - this keeps the host
// page's diff small and lets this card manage its own loading/error state.
export default function DueFollowUpsCard({ doctorId = null, branchId = null, canDismiss = true }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [pendingReminders, setPendingReminders] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dismissingId, setDismissingId] = useState(null);

  const fetchDue = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getDueFollowUps(doctorId, branchId);
      setItems(res.data?.items || []);
      setPendingReminders(res.data?.pending_patient_reminders || 0);
    } catch (err) {
      console.error("Failed to fetch due follow-ups", err);
    } finally {
      setLoading(false);
    }
  }, [doctorId, branchId]);

  useEffect(() => {
    fetchDue();
  }, [fetchDue]);

  const handleDismiss = async (id) => {
    setDismissingId(id);
    try {
      await api.updateFollowUpItem(id, { status: "done" });
      setItems((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      console.error("Failed to dismiss follow-up", err);
    } finally {
      setDismissingId(null);
    }
  };

  const handleBookAppointment = (item) => {
    navigate("/appointment-new", { state: { newPatientPhone: item.patient_phone } });
  };

  if (loading || items.length === 0) return null;

  return (
    <Card className="p-5">
      <div className="flex items-center gap-2 mb-4 px-1">
        <div className="flex items-center justify-center w-7 h-7 rounded-lg bg-amber-50 border border-amber-200 text-amber-600 shrink-0">
          <BellRing className="w-3.5 h-3.5" />
        </div>
        <h3 className="text-lg font-bold text-secondary-800">Follow-Ups Due</h3>
      </div>

      <div className="space-y-2">
        {items.map((item) => (
          <div key={item.id} className="p-3 rounded-xl border border-amber-100 bg-amber-50/40 space-y-1.5">
            {/* Names on the left (each cut off cleanly, full text on hover), actions on the right. */}
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-secondary-900 truncate" title={item.patient_name || ""}>
                  {item.patient_name || "Unknown patient"}
                </p>
                {item.doctor_name && (
                  <p className="text-xs text-secondary-500 truncate" title={item.doctor_name}>{item.doctor_name}</p>
                )}
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => handleBookAppointment(item)}
                  title="Book an appointment"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:text-emerald-900 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                >
                  <CalendarPlus className="w-3.5 h-3.5" />
                  Book
                </button>
                {canDismiss && (
                  <button
                    type="button"
                    onClick={() => handleDismiss(item.id)}
                    disabled={dismissingId === item.id}
                    title="Mark this follow-up as done"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-secondary-600 hover:text-secondary-900 bg-white hover:bg-secondary-50 border border-secondary-200 px-3 py-1.5 rounded-lg transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Dismiss
                  </button>
                )}
              </div>
            </div>
            {/* What it's about and how overdue - one full-width line. */}
            {(() => {
              const due = item.days_overdue > 0 ? `${item.days_overdue} day${item.days_overdue === 1 ? "" : "s"} overdue` : "Due today";
              const seen = item.sessiondate
                ? `last seen ${new Date(`${item.sessiondate}T00:00:00`).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}`
                : "";
              const full = [item.body, due, seen].filter(Boolean).join(" · ");
              return (
                <p className="text-xs truncate" title={full}>
                  {item.body && <span className="text-secondary-700">{item.body} · </span>}
                  <span className="text-amber-700 font-medium">{due}{seen ? ` · ${seen}` : ""}</span>
                </p>
              );
            })()}
          </div>
        ))}
      </div>

      {pendingReminders > 0 && (
        <p className="text-[11px] text-secondary-400 mt-3 px-1">
          {pendingReminders} patient reminder{pendingReminders === 1 ? "" : "s"} scheduled
        </p>
      )}
    </Card>
  );
}
